import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { UserIcon, CubeIcon, MapIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import {
  IProjectAsset,
  AssetType,
  CreateProjectAssetRequest,
  UpdateProjectAssetRequest,
  AssetGenerationStatus,
  IUserModel
} from '@packages/global';
import { useDialog } from '../../hooks/useDialog';
import { useAssetGenerationWebSocket } from '../../hooks/useAssetGenerationWebSocket';
import { useImageGenerationWebSocket } from '../../hooks/useImageGenerationWebSocket';
import { projectAssetApi } from '../../services/user/projectAsset.api';
import { modelApi } from '../../services/user/model.api';
import { useCoinStore } from '../../stores/coinStore';
import { SubjectAssetEditDrawer, AssetGenerateModalV2 } from '../projectAssets';
import UserAssetSelector from '../projectAssets/UserAssetSelector';
import { CompactModelSelector } from '../common/form';
import MediaPreviewModal from '../common/MediaPreviewModal';
import SubjectChatPanel from './SubjectChatPanel';
import SubjectGroup from './SubjectGroup';
import { extractBasePromptFromArtStyle } from '../../utils/artStyleExtractor';
import { toast } from 'react-hot-toast';

// 检查是否为认证角色
const isVerifiedCharacter = (asset: IProjectAsset): boolean => {
  if (asset.assetType !== AssetType.CHARACTER) return false;
  if (!asset.assetId) return false;
  if (!asset.conceptImageUrl) return false;

  // 检查 conceptImageUrl 中是否包含 assetId 参数
  try {
    const url = new URL(asset.conceptImageUrl, window.location.origin);
    const assetIdParam = url.searchParams.get('assetId');
    return !!assetIdParam;
  } catch {
    return false;
  }
};

interface SubjectSettingListProps {
  projectId: string;
  subjects: IProjectAsset[];
  loading?: boolean;
  onRefresh: () => void;
  projectArtStyle?: string;
}

export default function SubjectSettingList({
  projectId,
  subjects,
  loading: externalLoading,
  onRefresh,
  projectArtStyle = ''
}: SubjectSettingListProps) {
  const { t } = useTranslation(['projectSettings', 'common']);

  const assetTypeKey = (type: AssetType) => {
    const map: Record<AssetType, string> = {
      [AssetType.CHARACTER]: t('subject.character'),
      [AssetType.PROP]: t('subject.prop'),
      [AssetType.SCENE]: t('subject.scene'),
    };
    return map[type] || '';
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<IProjectAsset | null>(null);
  const [defaultAssetType, setDefaultAssetType] = useState<AssetType>(AssetType.CHARACTER);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generatingAsset, setGeneratingAsset] = useState<IProjectAsset | null>(null);
  const [generateMode, setGenerateMode] = useState<'text' | 'image'>('text');

  // 批量生成状态
  const [batchConfirm, setBatchConfirm] = useState<{ assetType: AssetType; assets: IProjectAsset[] } | null>(null);
  const [batchGenerating, setBatchGenerating] = useState<AssetType | null>(null);
  const [batchModels, setBatchModels] = useState<IUserModel[]>([]);
  const [batchModelId, setBatchModelId] = useState<string>('');
  const batchModelsLoadedRef = useRef(false);

  // 导入主体状态
  const [importSelectorOpen, setImportSelectorOpen] = useState(false);

  const { account } = useCoinStore();
  const balance = account?.balance ?? 0;
  const [localOverrides, setLocalOverrides] = useState<Record<string, Partial<IProjectAsset>>>({});
  const { confirm } = useDialog();

  const mergedSubjects = useMemo(() => subjects.map(s => {
    const o = localOverrides[s.id];
    return o ? { ...s, ...o } : s;
  }), [subjects, localOverrides]);

  const updateAssetLocal = useCallback((assetId: string, updates: Partial<IProjectAsset>) => {
    setLocalOverrides(prev => ({ ...prev, [assetId]: { ...prev[assetId], ...updates } }));
  }, []);

  const refreshAsset = useCallback(async (assetId: string) => {
    try {
      const data = await projectAssetApi.getProjectAssets(projectId);
      const updated = data.find(a => a.id === assetId);
      if (updated) {
        setLocalOverrides(prev => ({
          ...prev,
          [assetId]: {
            generationStatus: updated.generationStatus,
            conceptImageUrl: updated.conceptImageUrl,
            frontViewUrl: updated.frontViewUrl,
            prompt: updated.prompt
          }
        }));
      }
    } catch (err) {
      console.error(t('subject.refreshFailed'), err);
    }
  }, [projectId]);

  useAssetGenerationWebSocket({
    enabled: true,
    onAssetCompleted: useCallback((assetId: string) => {
      toast.success(t('subject.imageGenCompleted'));
      refreshAsset(assetId);
    }, [refreshAsset, t]),
    onAssetFailed: useCallback((assetId: string, error: string) => {
      toast.error(t('subject.imageGenFailed', { error }));
      updateAssetLocal(assetId, { generationStatus: AssetGenerationStatus.FAILED });
    }, [updateAssetLocal, t]),
    onProgressUpdate: useCallback((data: any) => {
      if (data.assetId) {
        updateAssetLocal(data.assetId, {
          generationStatus: data.status === 'completed'
            ? AssetGenerationStatus.COMPLETED
            : data.status === 'failed'
              ? AssetGenerationStatus.FAILED
              : AssetGenerationStatus.GENERATING
        });
      }
    }, [updateAssetLocal])
  });

  useImageGenerationWebSocket();

  const characterSubjects = mergedSubjects.filter(s => s.assetType === AssetType.CHARACTER);
  const propSubjects = mergedSubjects.filter(s => s.assetType === AssetType.PROP);
  const sceneSubjects = mergedSubjects.filter(s => s.assetType === AssetType.SCENE);

  const handleCreateAsset = (assetType: AssetType) => {
    setEditingAsset(null);
    setDefaultAssetType(assetType);
    setIsDrawerOpen(true);
  };

  const handleEditAsset = (asset: IProjectAsset) => {
    setEditingAsset(asset);
    setDefaultAssetType(asset.assetType);
    setIsDrawerOpen(true);
  };

  const handleSaveAsset = async (data: CreateProjectAssetRequest | UpdateProjectAssetRequest) => {
    const submitData = { ...data, projectId, episodeId: undefined };
    try {
      if (editingAsset) {
        await projectAssetApi.updateProjectAsset(editingAsset.id, submitData as UpdateProjectAssetRequest);
        toast.success(t('subject.assetUpdated'));
      } else {
        await projectAssetApi.createProjectAsset(submitData as CreateProjectAssetRequest);
        toast.success(t('subject.assetCreated'));
      }
      setIsDrawerOpen(false);
      setEditingAsset(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || t('subject.assetSaveFailed'));
    }
  };

  const handleDeleteAsset = async (asset: IProjectAsset) => {
    const confirmed = await confirm(t('subject.deleteSubjectConfirm', { name: asset.name }), t('common:deleteConfirmTitle'), 'danger');
    if (confirmed) {
      try {
        await projectAssetApi.deleteProjectAsset(asset.id);
        toast.success(t('subject.assetDeleted'));
        onRefresh();
      } catch (err: any) {
        toast.error(err.message || t('subject.assetDeleteFailed'));
      }
    }
  };

  const handleGenerateAsset = (asset: IProjectAsset) => {
    setGeneratingAsset(asset);
    setGenerateMode('text');
    setGenerateModalOpen(true);
  };

  const handleUploadAsset = (asset: IProjectAsset) => {
    setGeneratingAsset(asset);
    setGenerateMode('image');
    setGenerateModalOpen(true);
  };

  const handleImportAssets = useCallback(async (assets: IProjectAsset[]) => {
    let successCount = 0, failCount = 0;
    for (const asset of assets) {
      try {
        await projectAssetApi.createProjectAsset({
          projectId,
          assetType: asset.assetType,
          name: asset.name,
          prompt: asset.prompt,
          conceptImageUrl: asset.conceptImageUrl,
          frontViewUrl: asset.frontViewUrl,
          sideViewUrl: asset.sideViewUrl,
          backViewUrl: asset.backViewUrl,
          detailImageUrl: asset.detailImageUrl,
          description: asset.description,
          artStyle: asset.artStyle,
          role: asset.role,
          voiceReference: asset.voiceReference,
          voiceModelId: asset.voiceModelId,
          sortOrder: asset.sortOrder,
        });
        successCount++;
      } catch {
        failCount++;
      }
    }
    if (failCount === 0) {
      toast.success(t('subject.importSuccess', { count: successCount }));
    } else {
      toast.error(t('subject.partialSuccess', { success: successCount, fail: failCount }));
    }
    onRefresh();
  }, [projectId, onRefresh]);

  const ensureBatchModels = useCallback(async (): Promise<IUserModel[]> => {
    if (batchModelsLoadedRef.current) return batchModels;
    batchModelsLoadedRef.current = true;
    try {
      const models = await modelApi.getAllModels({ type: 'text2image' });
      setBatchModels(models);
      if (models.length > 0) setBatchModelId(models[0].id);
      return models;
    } catch (err: any) {
      toast.error(err.message || t('subject.loadModelFailed'));
      return [];
    }
  }, [batchModels]);

  const handleBatchGenerateClick = useCallback(async (assetType: AssetType, assets: IProjectAsset[]) => {
    const assetsWithoutImage = assets.filter(a => !a.conceptImageUrl && !a.frontViewUrl);
    const models = await ensureBatchModels();
    if (models.length === 0) { toast.error(t('subject.noModelsAvailable')); return; }
    setBatchConfirm({ assetType, assets: assetsWithoutImage });
  }, [ensureBatchModels]);

  const handleBatchGenerateConfirm = useCallback(async () => {
    if (!batchConfirm || !batchModelId) return;
    const { assetType, assets } = batchConfirm;
    setBatchConfirm(null);
    setBatchGenerating(assetType);
    let successCount = 0, failCount = 0;
    for (const asset of assets) {
      try {
        await projectAssetApi.generateAsset(asset.id, batchModelId);
        updateAssetLocal(asset.id, { generationStatus: AssetGenerationStatus.PENDING });
        successCount++;
      } catch (err: any) {
        failCount++;
      }
    }
    setBatchGenerating(null);
    failCount === 0
      ? toast.success(t('subject.submittedTasks', { count: successCount }))
      : toast.error(t('subject.partialSuccess', { success: successCount, fail: failCount }));
  }, [batchConfirm, batchModelId, updateAssetLocal]);

  const commonGroupProps = {
    onEdit: handleEditAsset,
    onDelete: handleDeleteAsset,
    onPreview: setPreviewImage,
    onGenerate: handleGenerateAsset,
    onUpload: handleUploadAsset,
  };

  if (externalLoading) {
    return (
      <div className="h-full flex gap-4 min-h-0">
        <div className="flex-1 flex flex-col gap-6 min-h-0 overflow-auto">
          {[3, 2, 2].map((count, i) => (
            <div key={i} className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="h-6 w-20 bg-border-subtle animate-pulse rounded" />
                <div className="h-8 w-24 bg-border-subtle animate-pulse rounded-lg" />
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                {Array.from({ length: count }).map((_, j) => (
                  <div key={j} className="h-28 bg-bg-surface rounded-xl animate-pulse border border-border-subtle" />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="w-[400px] flex-shrink-0 min-h-0">
          <div className="h-full bg-bg-surface rounded-xl border border-border-subtle animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex gap-4 min-h-0">
      {/* 左侧列表区 */}
      <div className="flex-1 flex flex-col gap-6 min-h-0 overflow-auto">
        <SubjectGroup
          title={t('subject.character')} icon={<UserIcon className="w-5 h-5 text-primary" />}
          subjects={characterSubjects} assetType={AssetType.CHARACTER}
          onAdd={() => handleCreateAsset(AssetType.CHARACTER)}
          onImport={() => setImportSelectorOpen(true)}
          batchGenerating={batchGenerating === AssetType.CHARACTER}
          onBatchGenerate={() => handleBatchGenerateClick(AssetType.CHARACTER, characterSubjects)}
          {...commonGroupProps}
        />
        <SubjectGroup
          title={t('subject.prop')} icon={<CubeIcon className="w-5 h-5 text-blue-400" />}
          subjects={propSubjects} assetType={AssetType.PROP}
          onAdd={() => handleCreateAsset(AssetType.PROP)}
          onImport={() => setImportSelectorOpen(true)}
          batchGenerating={batchGenerating === AssetType.PROP}
          onBatchGenerate={() => handleBatchGenerateClick(AssetType.PROP, propSubjects)}
          {...commonGroupProps}
        />
        <SubjectGroup
          title={t('subject.scene')} icon={<MapIcon className="w-5 h-5 text-green-400" />}
          subjects={sceneSubjects} assetType={AssetType.SCENE}
          onAdd={() => handleCreateAsset(AssetType.SCENE)}
          onImport={() => setImportSelectorOpen(true)}
          batchGenerating={batchGenerating === AssetType.SCENE}
          onBatchGenerate={() => handleBatchGenerateClick(AssetType.SCENE, sceneSubjects)}
          {...commonGroupProps}
        />
      </div>

      {/* 右侧 AI 聊天面板 */}
      <div className="w-[400px] flex-shrink-0 min-h-0">
        <SubjectChatPanel projectId={projectId} onSubjectsChange={onRefresh} />
      </div>

      {/* 编辑抽屉 */}
      <SubjectAssetEditDrawer
        isOpen={isDrawerOpen}
        onClose={() => { setIsDrawerOpen(false); setEditingAsset(null); }}
        title={editingAsset ? t('subject.editType', { type: assetTypeKey(defaultAssetType) }) : t('subject.addType', { type: assetTypeKey(defaultAssetType) })}
        initialData={editingAsset || undefined}
        defaultAssetType={defaultAssetType}
        projectId={projectId}
        onSave={handleSaveAsset}
      />

      {/* 图片预览 */}
      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image" src={previewImage || ''} alt={t('subject.previewAlt')}
      />

      {/* 生成弹窗 */}
      <AssetGenerateModalV2
        isOpen={generateModalOpen}
        onClose={() => { setGenerateModalOpen(false); setGeneratingAsset(null); }}
        subjectType={generatingAsset?.assetType || AssetType.CHARACTER}
        subjectName={generatingAsset?.name || ''}
        subjectDescription={generatingAsset?.description}
        initialPrompt={generatingAsset?.prompt}
        artStyle={projectArtStyle ? extractBasePromptFromArtStyle(projectArtStyle) : generatingAsset?.artStyle}
        existingImageUrl={generatingAsset?.conceptImageUrl || generatingAsset?.frontViewUrl}
        initialMode={generateMode}
        onConfirm={async (result) => {
          if (generatingAsset) {
            try {
              await projectAssetApi.updateProjectAsset(generatingAsset.id, {
                conceptImageUrl: result.imageUrl,
                prompt: result.prompt,
                artStyle: result.artStyle,
                ...(result.subjectDescription !== undefined && { description: result.subjectDescription })
              });
              toast.success(t('subject.settingUpdated'));
              updateAssetLocal(generatingAsset.id, { generationStatus: AssetGenerationStatus.COMPLETED });
              onRefresh();
            } catch (err: any) {
              toast.error(err.message || t('subject.settingUpdateFailed'));
            }
          }
        }}
        onSuccess={() => {
          if (generatingAsset) updateAssetLocal(generatingAsset.id, { generationStatus: AssetGenerationStatus.PENDING });
        }}
      />

      {/* 导入账号主体选择器 */}
      <UserAssetSelector
        isOpen={importSelectorOpen}
        onConfirm={handleImportAssets}
        onClose={() => setImportSelectorOpen(false)}
      />

      {/* 批量生成确认弹窗 */}
      {batchConfirm && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-bg-elevated border border-border-default rounded-2xl p-6 w-full max-w-sm shadow-xl">
          <div className="flex items-center gap-2 mb-4">
            <SparklesIcon className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold text-gray-50">{t('subject.batchTitle')}</h3>
          </div>
          <p className="text-sm text-gray-300 mb-4"
            dangerouslySetInnerHTML={{ __html: t('subject.batchDescription', { count: batchConfirm.assets.length, type: assetTypeKey(batchConfirm.assetType) }) }}
          />
          <div className="mb-4">
            <CompactModelSelector label={t('subject.selectModel')} models={batchModels} value={batchModelId} onChange={setBatchModelId} />
          </div>
          {(() => {
            const m = batchModels.find(m => m.id === batchModelId);
            const coinPrice = m?.coinPrice ?? 0;
            const totalCost = coinPrice * batchConfirm.assets.length;
            const enough = balance >= totalCost;
            return coinPrice > 0 ? (
              <>
                <p className="text-sm text-gray-400 mb-3">
                  {t('subject.estimatedCost')}
                  <span className={`font-medium ${enough ? 'text-yellow-400' : 'text-red-400'}`}>{totalCost} {t('subject.coins')}</span>
                  <span className="text-gray-500 ml-2">({t('subject.balance', { amount: balance })})</span>
                </p>
                {!enough && (
                  <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-red-400">{t('subject.insufficientBalance')}</span>
                  </div>
                )}
              </>
            ) : null;
          })()}
          <div className="flex gap-3">
            <button onClick={() => setBatchConfirm(null)}
              className="flex-1 px-4 py-2 bg-bg-elevated hover:bg-border-subtle text-gray-50 rounded-lg transition-colors text-sm cursor-pointer">
              {t('common:cancel')}
            </button>
            <button
              onClick={handleBatchGenerateConfirm}
              disabled={!batchModelId || (() => {
                const m = batchModels.find(m => m.id === batchModelId);
                return (m?.coinPrice ?? 0) > 0 && balance < (m?.coinPrice ?? 0) * batchConfirm.assets.length;
              })()}
              className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-4 w-4" />
              {t('subject.confirmGenerate')}
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
